import React, { useState, useContext, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../hooks/UserContext";
import { api } from "../../services/baseUrl";
import { ThemeContext } from "../../hooks/ThemeContext";

const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { email: "", password: "" } });

  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const { login } = useContext(UserContext);
  const emailRef = useRef(null);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    emailRef.current?.focus(); // focus direct sur l'email
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    setAuthError(null);
    try {
      const formData = new FormData();
      formData.append("email", data.email);
      formData.append("password", data.password);

      const res = await api.post("/login", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.status === 200) {
        localStorage.setItem("access_token", res.data.data.access_token.token);
        const userData = res.data.data.user;
        login(userData, userData?.id_role);
        navigate(from, { replace: true });
      }
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setAuthError(
        apiMessage ||
          "Échec de la connexion. Veuillez vérifier vos identifiants.",
      );
    } finally {
      setLoading(false);
    }
  };

  const g = "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)";

  return (
    <div
      className="relative flex items-center justify-center min-h-screen px-4 overflow-hidden"
      style={{ backgroundColor: isDark ? "#07070a" : "#f9fafb" }}
    >
      {/* Orbs */}
      {isDark && (
        <>
          <div
            className="absolute top-[-10%] left-[-5%] w-[420px] h-[420px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, #f43f5e33 0%, transparent 70%)",
              filter: "blur(80px)",
            }}
          />
          <div
            className="absolute bottom-[-10%] right-[-5%] w-[380px] h-[380px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, #6366f133 0%, transparent 70%)",
              filter: "blur(80px)",
            }}
          />
        </>
      )}

      <div
        className={`relative z-10 w-full max-w-md rounded-2xl p-8 border shadow-2xl ${
          isDark
            ? "bg-white/[0.04] border-white/[0.08] text-white"
            : "bg-white border-gray-200 text-gray-800 shadow-gray-200/60"
        }`}
      >
        {/* Badge */}
        <div className="flex justify-center mb-5">
          <span
            className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold border ${
              isDark
                ? "bg-white/[0.06] border-white/[0.10] text-gray-300"
                : "bg-rose-50 border-rose-200 text-rose-600"
            }`}
          >
            Bienvenue
          </span>
        </div>

        {/* Titre */}
        <h1 className="text-3xl font-bold text-center mb-1">
          {isDark ? (
            <>
              Connexion à{" "}
              <span
                style={{
                  backgroundImage: g,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                votre espace.
              </span>
            </>
          ) : (
            "Connexion à votre espace."
          )}
        </h1>
        <p
          className={`text-center text-sm mb-8 ${isDark ? "text-gray-400" : "text-gray-500"}`}
        >
          Accédez à vos projets, commandes et messages.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              ref={emailRef}
              {...register("email", { required: "Adresse mail obligatoire" })}
              className={`w-full px-4 py-2.5 border rounded-xl shadow-sm transition focus:outline-none focus:ring-2 focus:ring-rose-500 ${
                isDark
                  ? "bg-white/[0.05] border-white/[0.08] text-gray-800 placeholder-gray-500"
                  : "bg-gray-50 border-gray-300 text-gray-800"
              }`}
              placeholder="votre@email.com"
            />
            {errors.email && (
              <p className="text-xs text-rose-500 mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="password"
              className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
            >
              Mot de passe
            </label>
            <input
              id="password"
              type="password"
              {...register("password", {
                required: "Mot de passe obligatoire",
              })}
              className={`w-full px-4 py-2.5 border rounded-xl shadow-sm transition focus:outline-none focus:ring-2 focus:ring-rose-500 ${
                isDark
                  ? "bg-white/[0.05] border-white/[0.08] text-gray-800 placeholder-gray-500"
                  : "bg-gray-50 border-gray-300 text-gray-800"
              }`}
              placeholder="••••••••"
            />
            {errors.password && (
              <p className="text-xs text-rose-500 mt-1">
                {errors.password.message}
              </p>
            )}
          </div>

          {authError && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm">
              {authError}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 text-white font-semibold rounded-xl shadow-lg transition duration-200 disabled:opacity-60"
            style={{ background: g }}
          >
            {loading ? "Connexion en cours..." : "Se connecter"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/register")}
            className={`w-full py-2.5 px-4 font-medium rounded-xl border transition duration-200 text-sm ${
              isDark
                ? "border-white/[0.10] text-gray-300 hover:bg-white/[0.04]"
                : "border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            Pas encore de compte ?{" "}
            <span
              className="font-semibold"
              style={{
                backgroundImage: g,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              S'inscrire
            </span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
